import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Dropdown, ButtonGroup, Form, Modal, Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import ClinicalButton from '../../doctor/ui/ClinicalButton';
import receptionService from '../../../services/receptionService';
import externalMemberService from '../../../services/externalMemberService';
import { MEMBER_TYPES } from '../../receptionist/receptionConstants';
import { ORIENT_LABELS } from '../../receptionist/receptionConstants';
import { formatVisitDate, visitStatusLabel } from '../../receptionist/receptionFormatters';
import {
  formatMemberAge,
  formatMemberSexeShort,
  formatPersonProfileMeta,
  memberDisplayName
} from '../../receptionist/browse/browseFormatters';
import {
  ReceptionSearchAnchor,
  ReceptionFloatingResults,
  ReceptionFloatingResultItem
} from '../../receptionist/ui';
import ExternalMemberForm from '../../receptionist/panels/ExternalMemberForm';
import NurseIntakeConfirmModal from '../forms/NurseIntakeConfirmModal';
import { NURSE_COPY } from '../nurseCopy';
import { buildNurseIntakeConsultation } from '../utils/nurseIntakeConsultation';
import { dispatchNurseQueueRefresh } from '../utils/nurseQueueEvents';

const I = NURSE_COPY.DASHBOARD.INTAKE;

export default function NurseDashboardIntakePanel({ workSiteId, siteName = '', toast, onIntakeSuccess }) {
  const [memberSearch, setMemberSearch] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [externalMatches, setExternalMatches] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [searchResultsOpen, setSearchResultsOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [recentActivities, setRecentActivities] = useState([]);
  const [recentActivitiesLoading, setRecentActivitiesLoading] = useState(false);
  const [recentActivitiesMessage, setRecentActivitiesMessage] = useState('');

  const [showIntakeModal, setShowIntakeModal] = useState(false);
  const [visitNotes, setVisitNotes] = useState('');
  const [orientSymptoms, setOrientSymptoms] = useState('');
  const [intakeSubmitting, setIntakeSubmitting] = useState(false);

  const [showExternalMemberModal, setShowExternalMemberModal] = useState(false);
  const [externalMemberSaving, setExternalMemberSaving] = useState(false);
  const [externalMemberForm, setExternalMemberForm] = useState({
    nom: '',
    prenom: '',
    date_naissance: '',
    sexe: '',
    telephone: '',
    email: '',
    adresse: ''
  });

  const siteReady = workSiteId !== null && workSiteId !== undefined && String(workSiteId).trim() !== '';

  const getPatientDisplayName = useCallback((kind, member) => {
    const name = `${member?.prenom || ''} ${member?.nom || ''}`.trim();
    return name || member?.nom || 'Patient';
  }, []);

  const loadPatientRecentActivities = useCallback(async (kind, member) => {
    if (kind === 'SMIE' && member?.matricule) {
      setRecentActivitiesLoading(true);
      setRecentActivitiesMessage('');
      try {
        const historyRes = await receptionService.getVisitHistoryByMatricule(member.matricule, { limit: 5 });
        const rows = historyRes.success && Array.isArray(historyRes.data) ? historyRes.data : [];
        setRecentActivities(rows);
        setRecentActivitiesMessage(rows.length === 0 ? I.NO_RECENT : '');
      } catch (historyError) {
        setRecentActivities([]);
        setRecentActivitiesMessage(historyError.message || I.HISTORY_ERROR);
      } finally {
        setRecentActivitiesLoading(false);
      }
      return;
    }

    setRecentActivities([]);
    setRecentActivitiesLoading(false);
    setRecentActivitiesMessage(kind === 'EXTERNAL' ? I.EXTERNAL_HISTORY : I.NO_HISTORY);
  }, []);

  const resetPatientSelection = useCallback(() => {
    setSelectedPatient(null);
    setRecentActivities([]);
    setRecentActivitiesMessage('');
    setRecentActivitiesLoading(false);
  }, []);

  const selectPatient = useCallback(
    async (kind, member) => {
      setSelectedPatient({ kind, member });
      setSearchResultsOpen(false);
      setSearchError('');
      setMemberSearch(getPatientDisplayName(kind, member));
      await loadPatientRecentActivities(kind, member);
    },
    [getPatientDisplayName, loadPatientRecentActivities]
  );

  const changeSelectedPatient = useCallback(() => {
    resetPatientSelection();
    setMemberSearch('');
    setMatches([]);
    setExternalMatches([]);
    setSearchResultsOpen(false);
    setSearchError('');
    setSearchLoading(false);
  }, [resetPatientSelection]);

  const performPatientSearch = useCallback(
    async (rawQuery, { notifyEmpty = false } = {}) => {
      setSearchError('');
      const q = rawQuery.trim();
      if (q.length < 2) {
        if (notifyEmpty) setSearchError(I.SEARCH_MIN);
        setSearchResultsOpen(false);
        setMatches([]);
        setExternalMatches([]);
        return;
      }
      setSearchLoading(true);
      setSearchResultsOpen(true);
      try {
        const res = await receptionService.searchMembers(q, { limit: 30 });
        const extRes = await externalMemberService.list({ search: q, limit: 30, offset: 0 });
        const list = res.success ? res.data || [] : [];
        const extList = extRes.success ? extRes.data || [] : [];
        setMatches(list);
        setExternalMatches(extList);
        if (notifyEmpty && list.length === 0 && extList.length === 0) {
          toast('warning', I.SEARCH_EMPTY);
        }
      } catch (err) {
        setSearchError(err.message || I.SEARCH_ERROR);
        setMatches([]);
        setExternalMatches([]);
      } finally {
        setSearchLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    const q = memberSearch.trim();
    if (q.length < 2) {
      setSearchResultsOpen(false);
      setMatches([]);
      setExternalMatches([]);
      setSearchLoading(false);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      performPatientSearch(q);
    }, 320);

    return () => window.clearTimeout(timer);
  }, [memberSearch, performPatientSearch]);

  const handleSearch = async (e) => {
    e.preventDefault();
    await performPatientSearch(memberSearch, { notifyEmpty: true });
  };

  const clearPatientSearch = () => changeSelectedPatient();

  const openIntakeModal = () => {
    if (!siteReady) {
      toast('warning', I.NO_SITE);
      return;
    }
    if (!selectedPatient) return;
    setVisitNotes('');
    setOrientSymptoms('');
    setShowIntakeModal(true);
  };

  const submitIntake = async () => {
    if (!selectedPatient || !siteReady) return;
    const sid = parseInt(String(workSiteId), 10);
    if (!Number.isInteger(sid) || sid < 1) {
      toast('danger', I.INVALID_SITE);
      return;
    }

    const patientSnapshot = selectedPatient;

    setIntakeSubmitting(true);
    try {
      const payload = {
        site_id: sid,
        service_line: 'NURSING',
        symptoms: orientSymptoms.trim() || undefined,
        notes: visitNotes.trim() || undefined,
        ...(patientSnapshot.kind === 'SMIE'
          ? { member_id: patientSnapshot.member.id }
          : { external_member_id: patientSnapshot.member.id })
      };
      const res = await receptionService.registerAndOrientVisit(payload);
      if (!res.success) {
        throw new Error(res.message || I.INTAKE_ERROR);
      }

      setShowIntakeModal(false);
      toast('success', I.INTAKE_SUCCESS);

      const consultation = buildNurseIntakeConsultation({
        response: res,
        patientSnapshot,
        siteId: sid,
        getPatientDisplayName
      });

      changeSelectedPatient();
      dispatchNurseQueueRefresh(sid);
      onIntakeSuccess?.(consultation);
    } catch (err) {
      toast('danger', err.message || I.INTAKE_ERROR);
    } finally {
      setIntakeSubmitting(false);
    }
  };

  const resetExternalMemberForm = () => {
    setExternalMemberForm({
      nom: '',
      prenom: '',
      date_naissance: '',
      sexe: '',
      telephone: '',
      email: '',
      adresse: ''
    });
  };

  const submitExternalMember = async (e) => {
    e.preventDefault();
    setExternalMemberSaving(true);
    try {
      const payload = {
        nom: externalMemberForm.nom.trim(),
        prenom: externalMemberForm.prenom.trim() || null,
        date_naissance: externalMemberForm.date_naissance || null,
        sexe: externalMemberForm.sexe || null,
        telephone: externalMemberForm.telephone.trim() || null,
        email: externalMemberForm.email.trim() || null,
        adresse: externalMemberForm.adresse.trim() || null
      };
      const res = await externalMemberService.create(payload);
      if (res.success) {
        toast('success', I.EXTERNAL_CREATED);
        setShowExternalMemberModal(false);
        resetExternalMemberForm();
      }
    } catch (err) {
      toast('danger', err.message || I.EXTERNAL_ERROR);
    } finally {
      setExternalMemberSaving(false);
    }
  };

  const selectedPatientProfile = useMemo(() => {
    if (!selectedPatient) return null;
    const { kind, member } = selectedPatient;
    const displayName = getPatientDisplayName(kind, member);
    const typeLabel =
      kind === 'EXTERNAL'
        ? member.statut || 'Membre externe'
        : MEMBER_TYPES[member.type] || member.type || 'Adhérent SMIE';
    const details = [
      member.matricule ? `Matricule ${member.matricule}` : null,
      member.company_name || null,
      typeLabel,
      member.telephone || null,
      formatMemberSexeShort(member.sexe) !== '—' ? formatMemberSexeShort(member.sexe) : null,
      formatMemberAge(member.date_naissance) !== '—' ? formatMemberAge(member.date_naissance) : null
    ].filter(Boolean);

    return { displayName, typeLabel, details };
  }, [getPatientDisplayName, selectedPatient]);

  const patientSearchOpen = searchResultsOpen && memberSearch.trim().length >= 2;

  return (
    <>
      <Card
        className={`border-0 recv-dash-card h-100 recv-dash-card--elevated recv-dash-card--primary recv-intake-card nr-intake-card${
          patientSearchOpen ? ' is-search-open' : ''
        }`}
      >
        <Card.Header className="recv-dash-card__head recv-intake-card__head border-0 bg-transparent mb-0">
          <span className="recv-dash-card__eyebrow">{I.EYEBROW}</span>
          <Card.Title className="h5 mb-0 fw-semibold mt-2 text-body">{I.TITLE}</Card.Title>
          <Card.Subtitle className="text-muted small mt-2 mb-0 fw-normal">
            {siteName ? (
              <>
                Site <strong>{siteName}</strong> — {I.SUBTITLE_SITE}
              </>
            ) : (
              I.SUBTITLE
            )}
          </Card.Subtitle>
        </Card.Header>
        <Card.Body className="recv-intake-card__body">
          {!siteReady ? (
            <p className="text-muted small mb-0">{I.NO_SITE}</p>
          ) : (
            <Form onSubmit={handleSearch} className="recv-intake-form">
              <div className="recv-intake-form__topline">
                <Form.Label className="recv-intake-form__label mb-0">{I.SEARCH_LABEL}</Form.Label>
                {memberSearch.trim().length >= 2 && (
                  <span className="recv-intake-form__count">
                    {matches.length + externalMatches.length} résultat(s)
                  </span>
                )}
              </div>
              <div className="recv-intake-search-row">
                <ReceptionSearchAnchor className="recv-intake-search">
                  <span className="recv-intake-search__icon" aria-hidden>
                    <Icon name="search" size={18} />
                  </span>
                  <Form.Control
                    className="recv-intake-search__input"
                    value={memberSearch}
                    onChange={(ev) => {
                      const v = ev.target.value;
                      if (
                        selectedPatient &&
                        v.trim() !== getPatientDisplayName(selectedPatient.kind, selectedPatient.member)
                      ) {
                        resetPatientSelection();
                        setMatches([]);
                        setExternalMatches([]);
                      }
                      setMemberSearch(v);
                      if (v.trim().length < 2) {
                        setSearchResultsOpen(false);
                        setSearchError('');
                      } else {
                        setSearchResultsOpen(true);
                      }
                    }}
                    placeholder={I.SEARCH_PLACEHOLDER}
                    autoComplete="off"
                    aria-controls="nurse-dash-member-search-list"
                    aria-expanded={searchResultsOpen}
                    disabled={!siteReady}
                  />
                  {memberSearch ? (
                    <button
                      type="button"
                      className="recv-intake-search__clear"
                      onClick={clearPatientSearch}
                      aria-label={I.CLEAR_SEARCH}
                    >
                      <Icon name="times" size={14} />
                    </button>
                  ) : null}
                  <ReceptionFloatingResults
                    listId="nurse-dash-member-search-list"
                    show={patientSearchOpen}
                    loading={searchLoading}
                    emptyMessage={I.SEARCH_EMPTY}
                  >
                    {matches.length > 0 && (
                      <>
                        <div className="recv-floating-results__title">{I.SMIE_RESULTS}</div>
                        {matches.map((m) => (
                          <ReceptionFloatingResultItem
                            key={m.id}
                            primary={memberDisplayName(m)}
                            secondary={m.company_name || undefined}
                            meta={formatPersonProfileMeta({
                              matricule: m.matricule,
                              typeLabel: MEMBER_TYPES[m.type] || m.type,
                              sexe: m.sexe,
                              date_naissance: m.date_naissance
                            })}
                            actionLabel={I.SELECT}
                            selected={
                              selectedPatient?.kind === 'SMIE' &&
                              String(selectedPatient.member.id) === String(m.id)
                            }
                            onSelect={() => selectPatient('SMIE', m)}
                          />
                        ))}
                      </>
                    )}
                    {externalMatches.length > 0 && (
                      <>
                        <div className="recv-floating-results__title">{I.EXTERNAL_RESULTS}</div>
                        {externalMatches.map((m) => (
                          <ReceptionFloatingResultItem
                            key={`ext-${m.id}`}
                            primary={memberDisplayName(m) || m.nom}
                            meta={formatPersonProfileMeta({
                              leading: [m.telephone, m.statut || 'EXTERNE'].filter(Boolean),
                              sexe: m.sexe,
                              date_naissance: m.date_naissance
                            })}
                            actionLabel={I.SELECT}
                            selected={
                              selectedPatient?.kind === 'EXTERNAL' &&
                              String(selectedPatient.member.id) === String(m.id)
                            }
                            onSelect={() => selectPatient('EXTERNAL', m)}
                          />
                        ))}
                      </>
                    )}
                  </ReceptionFloatingResults>
                </ReceptionSearchAnchor>
                <Button
                  type="submit"
                  variant="primary"
                  className="recv-intake-search__submit"
                  disabled={searchLoading || !siteReady}
                >
                  {searchLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      {I.SEARCHING}
                    </>
                  ) : (
                    I.SEARCH_BUTTON
                  )}
                </Button>
              </div>
              <div className="recv-intake-search__hint">
                {selectedPatient ? I.HINT_SELECTED : I.HINT_SEARCH}
              </div>
              {searchError && <div className="text-danger small mt-2">{searchError}</div>}

              {selectedPatient && selectedPatientProfile ? (
                <div className="recv-patient-card" aria-live="polite">
                  <div className="recv-patient-card__head">
                    <div className="recv-patient-card__identity">
                      <span className="recv-patient-card__badge">{I.PATIENT_IDENTIFIED}</span>
                      <h3 className="recv-patient-card__name">{selectedPatientProfile.displayName}</h3>
                      <p className="recv-patient-card__details mb-0">
                        {selectedPatientProfile.details.join(' · ')}
                      </p>
                    </div>
                    <button type="button" className="recv-patient-card__change" onClick={changeSelectedPatient}>
                      {I.CHANGE_PATIENT}
                    </button>
                  </div>

                  <div className="recv-recent-activity recv-patient-card__history">
                    <div className="recv-recent-activity__head">
                      <div>
                        <span className="recv-recent-activity__eyebrow">{I.RECENT_EYEBROW}</span>
                        <h4 className="recv-recent-activity__title mb-0">{I.RECENT_TITLE}</h4>
                      </div>
                      {recentActivities.length > 0 && (
                        <span className="recv-recent-activity__count">{recentActivities.length}/5</span>
                      )}
                    </div>
                    {recentActivitiesLoading ? (
                      <div className="recv-recent-activity__loading">
                        <Spinner animation="border" size="sm" />
                        <span>{I.RECENT_LOADING}</span>
                      </div>
                    ) : recentActivities.length > 0 ? (
                      <div className="recv-recent-activity__list">
                        {recentActivities.map((activity) => (
                          <article key={activity.id} className="recv-recent-activity__item">
                            <div className="recv-recent-activity__date">
                              {formatVisitDate(activity.registered_at)}
                            </div>
                            <div className="recv-recent-activity__body">
                              <div className="recv-recent-activity__main">
                                {activity.service_line
                                  ? ORIENT_LABELS[activity.service_line] || activity.service_line
                                  : 'Passage réception'}
                              </div>
                              <div className="recv-recent-activity__meta">
                                {[activity.company_name, activity.visit_site_name, activity.registered_by_name]
                                  .filter(Boolean)
                                  .join(' · ') || '—'}
                              </div>
                            </div>
                            <span
                              className={`recv-recent-activity__status ${
                                activity.status === 'ORIENTED' ? 'is-ok' : 'is-pending'
                              }`}
                            >
                              {visitStatusLabel(activity.status)}
                            </span>
                          </article>
                        ))}
                      </div>
                    ) : (
                      <p className="recv-recent-activity__empty mb-0">
                        {recentActivitiesMessage || I.NO_RECENT}
                      </p>
                    )}
                  </div>

                  <div className="recv-patient-card__actions">
                    <ClinicalButton variant="primary" onClick={openIntakeModal}>
                      {I.WELCOME_BUTTON}
                    </ClinicalButton>
                  </div>
                </div>
              ) : null}

              <div className="recv-intake-actions" aria-label={I.QUICK_ACTIONS_ARIA}>
                <Dropdown as={ButtonGroup} className="recv-intake-actions__group">
                  <Button variant="outline-success" onClick={() => setShowExternalMemberModal(true)}>
                    <Icon name="plus" size={14} />
                    {I.ADD_EXTERNAL}
                  </Button>
                  <Dropdown.Toggle split variant="outline-success" id="nurse-dash-create-menu" />
                  <Dropdown.Menu align="end">
                    <Dropdown.Item onClick={() => setShowExternalMemberModal(true)}>
                      {I.ADD_EXTERNAL_MENU}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>

      <NurseIntakeConfirmModal
        show={showIntakeModal}
        onHide={() => setShowIntakeModal(false)}
        saving={intakeSubmitting}
        onSubmit={submitIntake}
        patientProfile={selectedPatientProfile}
        siteName={siteName}
        visitNotes={visitNotes}
        onVisitNotesChange={setVisitNotes}
        orientSymptoms={orientSymptoms}
        onOrientSymptomsChange={setOrientSymptoms}
      />

      <Modal show={showExternalMemberModal} onHide={() => setShowExternalMemberModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{I.EXTERNAL_MODAL_TITLE}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ExternalMemberForm
            value={externalMemberForm}
            onChange={setExternalMemberForm}
            onSubmit={submitExternalMember}
            onCancel={() => setShowExternalMemberModal(false)}
            saving={externalMemberSaving}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
